import { z } from 'zod'
import { zCount, zPence } from '@gridfit/core/money'
import { commandShape, queryShape } from './types'

/**
 * Browser-safe declarations of the toy slice. `packages/contracts/src/toy.ts`
 * spreads these into `defineCommand`/`defineQuery` and adds the handlers.
 */

const tallyRow = z.object({
  key: z.string(),
  /**
   * `z.int()`, NOT `zCount()`. `ToyCompensated` subtracts, so a tally row's
   * total is legitimately negative — `zCount()` refuses anything below zero
   * and would turn a correct fold state into a parse failure.
   */
  total: z.int(),
})

const quoteRow = z.object({
  quoteId: z.string(),
  number: z.string(),
  lineCount: z.int(),
  totalPence: zPence(),
})

export const toyTallyQuery = queryShape({
  name: 'toy.tally',
  params: z.object({ key: z.string() }),
  result: tallyRow.nullable(),
  capabilities: ['ops'],
})

export const toyQuoteQuery = queryShape({
  name: 'toy.quote',
  params: z.object({ quoteId: z.string() }),
  result: quoteRow.nullable(),
  capabilities: ['ops'],
})

export const toyPipelineQuery = queryShape({
  name: 'toy.pipeline',
  params: z.object({}),
  result: z.array(quoteRow),
  capabilities: ['ops'],
})

export const toyQuoteDraft = commandShape({
  name: 'toy.quote.draft',
  payload: z.object({
    quoteId: z.string(),
    lines: z.array(z.object({ description: z.string(), qty: zCount(), unitPence: zPence() })),
  }),
  capabilities: ['commercial'],
  // Commercial screens are online-only by design (architecture §8).
  offline: false,
})

export const toyTallyBump = commandShape({
  name: 'toy.tally.bump',
  payload: z.object({ key: z.string(), amount: zCount() }),
  capabilities: ['ops'],
  // The offline drill's command (design §8.2, Stage B). Site-facing, so
  // queueable — and it is the only shape in the repo that exercises the
  // outbox path until 160 and 170 arrive.
  offline: true,
})

export const toyTallyRetire = commandShape({
  name: 'toy.tally.retire',
  payload: z.object({ key: z.string(), thenBumpBy: zCount().optional() }),
  capabilities: ['ops'],
  offline: false,
})

export const toySeqAllocate = commandShape({
  name: 'toy.seq.allocate',
  payload: z.object({ sequence: z.string() }),
  capabilities: ['ops'],
  offline: false,
})
